import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Drawer, Table, Segmented, Input, Button, Typography, notification } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import lib from "../../assets/utils/lib";

const { Text } = Typography;

const KIND_COPY = {
  variance: {
    title: "PA variance",
    intro: "Approved PA lines whose approved amount differs from the requested amount. Variance = approved − requested.",
  },
  rejected: {
    title: "Rejected PA lines",
    intro: "Rejected PA lines at their requested amount.",
  },
};

export function signedMoney(value, money) {
  const n = Number(value) || 0;
  if (n === 0) return money(0);
  return `${n > 0 ? "+" : "−"}${money(Math.abs(n))}`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = dayjs(value);
  return d.isValid() ? d.format("MMM D, YYYY") : String(value);
}

/**
 * Drill-down list of PA lines behind the Variance / Rejected figures
 * (GET /v2/admin/pa/analytics/items), under the dashboard's filters.
 */
export default function PAItemsDrawer({ open, kind, params, formatMoney, onClose, onOpenEncounter }) {
  const [direction, setDirection] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({});
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState(null);
  const [exporting, setExporting] = useState(false);

  const isVariance = kind === "variance";
  const copy = KIND_COPY[kind] || KIND_COPY.variance;

  useEffect(() => {
    if (!open) return;
    setDirection("all");
    setSearch("");
    setPage(1);
    setSort({});
  }, [open, kind]);

  const query = useMemo(() => ({
    ...params,
    kind,
    direction: isVariance && direction !== "all" ? direction : undefined,
    search: search || undefined,
    sort_by: sort.sort_by,
    sort_dir: sort.sort_dir,
  }), [params, kind, isVariance, direction, search, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await lib.getPAAnalyticsItems({ ...query, page, pageSize });
      const body = resp?.data;
      if (body && Number(body.error) === 0) {
        setRows(Array.isArray(body.data?.items) ? body.data.items : []);
        setTotal(Number(body.data?.pagination?.total) || 0);
        setTotals(body.data?.totals || null);
      } else {
        setRows([]);
        setTotal(0);
        setTotals(null);
      }
    } catch (e) {
      console.error("Error loading PA lines", e);
      setRows([]);
      setTotal(0);
      setTotals(null);
    } finally {
      setLoading(false);
    }
  }, [query, page, pageSize]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const download = async () => {
    setExporting(true);
    try {
      await lib.downloadPAAnalyticsExport({ ...query, dataset: kind });
    } catch (e) {
      notification.error({ message: "Download failed", description: e?.message || "Could not export PA lines" });
    } finally {
      setExporting(false);
    }
  };

  const sortOrder = (key) => (sort.sort_by === key ? (sort.sort_dir === "asc" ? "ascend" : "descend") : null);

  const columns = [
    {
      title: "Encounter",
      dataIndex: "checkin_id",
      key: "checkin_id",
      width: 170,
      render: (v) => <Text code className="pa-items__code">{v}</Text>,
    },
    {
      title: "Request date",
      dataIndex: "request_date",
      key: "request_date",
      width: 118,
      sorter: true,
      sortOrder: sortOrder("request_date"),
      render: formatDate,
    },
    {
      title: "Client",
      key: "client",
      width: 170,
      render: (_, r) => {
        const c = r.client || {};
        const name = [c.surname, c.first_name].filter(Boolean).join(", ");
        return (
          <div className="pa-items__client">
            <span>{name || "—"}</span>
            {c.insurance_no ? <span className="pa-items__sub">{c.insurance_no}</span> : null}
          </div>
        );
      },
    },
    {
      title: "Organization",
      dataIndex: "organization_name",
      key: "organization_name",
      width: 160,
      ellipsis: true,
      render: (v) => v || "No organization (self-pay)",
    },
    {
      title: "Facility",
      dataIndex: "facility_name",
      key: "facility_name",
      width: 170,
      ellipsis: true,
      render: (v) => v || "—",
    },
    {
      title: "Item",
      dataIndex: "item_name",
      key: "item_name",
      width: 180,
      ellipsis: true,
      render: (v) => v || "—",
    },
    {
      title: "Requested",
      dataIndex: "requested_amount",
      key: "requested",
      width: 116,
      align: "right",
      sorter: true,
      sortOrder: sortOrder("requested"),
      render: (v) => formatMoney(v),
    },
    ...(isVariance
      ? [
          {
            title: "Approved",
            dataIndex: "approved_amount",
            key: "approved",
            width: 116,
            align: "right",
            sorter: true,
            sortOrder: sortOrder("approved"),
            render: (v) => formatMoney(v),
          },
          {
            title: "Variance",
            dataIndex: "variance_amount",
            key: "variance",
            width: 116,
            align: "right",
            sorter: true,
            sortOrder: sortOrder("variance"),
            render: (v) => (
              <span className={`pa-items__variance ${Number(v) < 0 ? "is-down" : "is-up"}`}>
                {signedMoney(v, formatMoney)}
              </span>
            ),
          },
        ]
      : []),
  ];

  const summary = totals ? (
    isVariance ? (
      <div className="pa-items__totals">
        <span><strong>{totals.items.toLocaleString()}</strong> lines</span>
        <span>Net <strong>{signedMoney(totals.variance_net, formatMoney)}</strong></span>
        <span className="is-down">Reductions <strong>{formatMoney(totals.variance_reductions)}</strong></span>
        <span className="is-up">Increases <strong>{formatMoney(totals.variance_increases)}</strong></span>
      </div>
    ) : (
      <div className="pa-items__totals">
        <span><strong>{totals.items.toLocaleString()}</strong> lines</span>
        <span>Requested <strong>{formatMoney(totals.requested_amount)}</strong></span>
        <span>{totals.encounters.toLocaleString()} encounters</span>
      </div>
    )
  ) : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={Math.min(1180, typeof window !== "undefined" ? window.innerWidth : 1180)}
      title={copy.title}
      destroyOnClose
      extra={(
        <Button size="small" icon={<DownloadOutlined />} loading={exporting} onClick={download}>
          CSV
        </Button>
      )}
    >
      <div className="pa-items">
        <p className="pa-items__intro">{copy.intro}</p>
        {summary}
        <div className="pa-items__toolbar">
          {isVariance ? (
            <Segmented
              size="small"
              value={direction}
              onChange={(v) => {
                setDirection(v);
                setPage(1);
              }}
              options={[
                { label: "All", value: "all" },
                { label: "Reductions", value: "reduction" },
                { label: "Increases", value: "increase" },
              ]}
            />
          ) : <span />}
          <Input.Search
            allowClear
            size="small"
            placeholder="Encounter, client, facility, organization"
            onSearch={(v) => {
              setSearch(v.trim());
              setPage(1);
            }}
            className="pa-items__search"
          />
        </div>
        <Table
          size="small"
          rowKey="line_id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: columns.reduce((sum, c) => sum + (Number(c.width) || 0), 0) }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100],
            showTotal: (t) => `${t.toLocaleString()} line${t === 1 ? "" : "s"}`,
          }}
          onChange={(pagination, _filters, sorter) => {
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            const next = s?.order
              ? { sort_by: s.columnKey, sort_dir: s.order === "ascend" ? "asc" : "desc" }
              : {};
            const sortChanged = next.sort_by !== sort.sort_by || next.sort_dir !== sort.sort_dir;
            if (sortChanged) setSort(next);
            setPage(sortChanged ? 1 : pagination.current || 1);
            setPageSize(pagination.pageSize || pageSize);
          }}
          onRow={(record) => ({
            onClick: () => onOpenEncounter?.(record),
            style: { cursor: onOpenEncounter ? "pointer" : "default" },
          })}
          locale={{ emptyText: isVariance ? "No PA lines approved at a different amount" : "No rejected PA lines" }}
        />
      </div>
    </Drawer>
  );
}
